import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  User,
  Info,
  HelpCircle,
  ChevronRight,
  CreditCardIcon,
  TicketCheck,
  Bell,
} from "lucide-react";
import { useFeatureFlagEnabled } from "posthog-js/react";
import referralFriends from "@/assets/images/referral-friends.webp";
import { useRootStore } from "@/state/RootStore";
import { USER_ACCESS_LEVEL } from "@/utils/types";
import { useNavigate } from "react-router";
import { APP_CONFIG, APP_TO_BUILD } from "../../../appsConfig";
import { useClerk } from "@clerk/clerk-react";
import { observer } from "mobx-react";
import { getUserAccessLabel } from "@/utils/purchaseState";
import { getLocalizedAccessLabel } from "@/i18n/accessLevelCopy";
import { canShowReferralInvite } from "@/utils/appEntitlements";
import AdminMode from "./AdminMode";
import { useToggle } from "react-use";
import { Trans, useTranslation } from "react-i18next";
import { useLogout } from "@/hooks/useLogout";
import { shouldShowManagePayments } from "./profilePaymentsVisibility";
import LanguageSelector from "@/components/LanguageSelector";
import RedeemCodeModal from "@/components/redemption/RedeemCodeModal";
import ShareEmployerLink from "./ShareEmployerLink";
import { getOrgExamTypeForApp } from "@/utils/orgPurchases/config";
import { getOrgRedeemSuccessRoute } from "@/utils/orgPurchases/redemptionRouting";
import { useIsMobileWeb } from "@/hooks/useIsMobileWeb";
import { DOWNLOAD_APP_CTA_CLEARANCE_CLASS } from "@/components/mobile/DownloadAppCta";
import { PRIVACY_POLICY_URL, TERMS_URL } from "@/utils/legalLinks";
import {
  clearPendingAccessCode,
  readPendingAccessCode,
} from "@/utils/orgPurchases/pendingAccessCode";
import * as Sentry from "@sentry/react";
import DesktopProfilePage from "./DesktopProfilePage";

const ProfilePage = observer(() => {
  const { t } = useTranslation();
  const store = useRootStore();
  const navigate = useNavigate();
  const { user } = useClerk();
  const { handleSignOut } = useLogout();
  const isMobileWeb = useIsMobileWeb();

  const [provider, setProvider] = useState<string | null>(null);
  const [showRedeemCode, toggleShowRedeemCode] = useToggle(false);
  const orgExamType = useMemo(() => getOrgExamTypeForApp(APP_TO_BUILD), []);
  const referralFlagEnabled = useFeatureFlagEnabled("refer-a-friend") === true;
  const showReferralRow = canShowReferralInvite({
    appHasReferralProgram: !!APP_CONFIG.referral,
    isPaidReferrer: store.mainAppStore.isPaidReferrer(),
    flagEnabled: referralFlagEnabled,
  });

  useEffect(() => {
    if (!user) {
      if (provider !== null) {
        setProvider(null);
      }
      return;
    }

    const externalAccounts = user.externalAccounts;
    let newProvider: string | null = null;

    if (externalAccounts && externalAccounts.length > 0) {
      newProvider = externalAccounts[0].provider;
    } else if (!externalAccounts?.length) {
      newProvider = "email";
    }

    if (newProvider !== provider) {
      setProvider(newProvider);
    }
  }, [user?.id, user?.externalAccounts?.length, provider]);

  const {
    rawAccessLabel,
    userAccessLabel,
    isProfileComplete,
    showManagePayments,
  } = useMemo(() => {
      const raw = getUserAccessLabel(
        store.mainAppStore.newPurchaseState?.purchaseAccess,
      );
      return {
        rawAccessLabel: raw,
        userAccessLabel: getLocalizedAccessLabel(t, raw),
        isProfileComplete: store.mainAppStore.isProfileComplete,
        showManagePayments: shouldShowManagePayments({
          purchaseAccess: store.mainAppStore.newPurchaseState?.purchaseAccess,
          purchasedProducts:
            store.mainAppStore.newPurchaseState?.purchasedProducts,
        }),
      };
    }, [
      store.mainAppStore.isProfileComplete,
      store.mainAppStore.newPurchaseState?.purchaseAccess,
      store.mainAppStore.newPurchaseState?.purchasedProducts,
      t,
    ]);

  const handleOrgCodeRedeemed = useCallback(
    async (redeemedCode: string) => {
      const pending = readPendingAccessCode();
      if (pending?.code === redeemedCode && pending.examType === orgExamType) {
        clearPendingAccessCode(pending);
      }
      const successRoute = getOrgRedeemSuccessRoute(
        store.mainAppStore.isProfileComplete,
        { isMobileWeb },
      );
    toggleShowRedeemCode(false);
      navigate(successRoute, { replace: true, flushSync: true });
      try {
        await store.mainAppStore.checkPurchaseState();
      } catch (error) {
        Sentry.captureException(error, {
          tags: {
            area: "org_access_code",
            phase: "manual_refresh_after_success",
          },
    });
      }
    return true;
    },
    [
      isMobileWeb,
      navigate,
      orgExamType,
      store.mainAppStore,
      toggleShowRedeemCode,
    ],
  );

  const handleProfileClick = useCallback(() => {
    const route = isProfileComplete ? "/edit-profile" : "/create-profile";
    navigate(route);
  }, [isProfileComplete, navigate]);

  const openRedeemCodeModal = useCallback(() => {
    toggleShowRedeemCode(true);
  }, [toggleShowRedeemCode]);

  const redeemCodeModal = orgExamType ? (
    <RedeemCodeModal
      examType={orgExamType}
      open={showRedeemCode}
      onOpenChange={toggleShowRedeemCode}
      onLegacyRedeem={
        APP_TO_BUILD === "electrical_us"
          ? (code) => store.mainAppStore.redeemCode(code)
          : undefined
      }
      onRedeemed={handleOrgCodeRedeemed}
      getBlockedReason={store.mainAppStore.getOrgRedeemBlockedReason}
    />
  ) : null;

  if (!isMobileWeb) {
    const fullName = user?.fullName || store.mainAppStore.profileName || t("profile.page_header_default");
    const email = user?.primaryEmailAddress?.emailAddress || store.mainAppStore.profileEmail || "";

    return (
      <>
        <DesktopProfilePage
          fullName={fullName}
          email={email}
          provider={provider}
          accessLabel={userAccessLabel}
          accessDescription={rawAccessLabel === "AI" ? t("profile.full_ai_access") : t("profile.full_access")}
          isFree={rawAccessLabel === USER_ACCESS_LEVEL.FREE}
          isProfileComplete={isProfileComplete}
          showManagePayments={showManagePayments}
          showReferral={showReferralRow}
          isAdmin={store.mainAppStore.isAdmin()}
          orgExamType={orgExamType}
          onEditProfile={handleProfileClick}
          onNotificationSettings={() => navigate("/profile/notification-settings")}
          onManagePayments={() => navigate("/profile/manage-payments")}
          onRedeemCode={openRedeemCodeModal}
          onReferral={() => navigate("/referral")}
          onUpgrade={() => navigate("/paywall")}
          onFaqs={() => {
            const url = APP_TO_BUILD === "electrical_us"
              ? "http://dakotaprep.com/faq"
              : "https://dakotaprep.com/ca/faq";
            window.open(url, "_blank", "noopener,noreferrer");
          }}
          onNeedHelp={() => window.open("mailto:support@dakotaprep.com", "_blank", "noopener,noreferrer")}
          onSignOut={handleSignOut}
        />
        {redeemCodeModal}
      </>
    );
  }

  return (
    <>
      <div
        className={`grow flex justify-center ${
          isMobileWeb
            ? `px-6 py-8 ${DOWNLOAD_APP_CTA_CLEARANCE_CLASS}`
            : "px-4 sm:px-8 py-12"
        }`}
      >
        <div
          className={
            isMobileWeb
              ? "w-full max-w-[594px] flex flex-col gap-6"
              : "w-full max-w-[594px] flex flex-col gap-9"
          }
        >
          {/* User Profile Header */}
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-3">
              <h2 className="text-black text-xl font-bold font-inter">
                {store.mainAppStore.profileName ??
                  t("profile.page_header_default")}
              </h2>
              {rawAccessLabel !== USER_ACCESS_LEVEL.FREE && (
                <div className="h-[15px] rounded-sm flex items-center justify-center border-[#FFB300] border-solid border bg-[#FFF0CD] px-[4px] w-fit rounded-tl-[2px] rounded-tr-[2px] rounded-bl-[2px]">
                  <span className="text-[10px] font-bold text-[#FFB300] whitespace-nowrap">
                    {userAccessLabel}
                  </span>
                </div>
              )}
            </div>
            <p className="text-[#9a9a9a] text-base font-normal">
              {user?.emailAddresses[0].emailAddress}
            </p>
            <p className="text-[#9a9a9a] text-sm font-normal">
              <Trans
                i18nKey="profile.managed_by_provider"
                values={{ provider }}
                components={{
                  provider: <span className="capitalize italic" />,
                }}
              />
            </p>
          </div>

          {/* Invite a Friend — mirrors the mobile profile card. The darker-blue
              bottom border gives the stacked-card depth from the design. */}
          {showReferralRow && !isMobileWeb && (
            <button
              onClick={() => navigate("/referral")}
              className="flex w-full items-center gap-3 rounded-2xl border-b-[6px] border-b-[#85C6E8] bg-[#BFE3F5] px-5 py-[18px] text-left transition-opacity hover:opacity-90"
            >
              <div className="flex flex-1 flex-col">
                <span className="text-xl font-bold font-inter text-[#232323]">
                  {t("referral.invite_card_title")}
                </span>
                <span className="mt-2 text-[15px] font-semibold leading-5 text-[#3C4A57]">
                  {t("referral.invite_card_description")}
                </span>
              </div>
              <img
                src={referralFriends}
                alt=""
                className="h-24 w-[110px] shrink-0 object-contain"
              />
            </button>
          )}

          {isMobileWeb && orgExamType && (
            <button
              onClick={openRedeemCodeModal}
              className="px-4 py-4 bg-white rounded-xl shadow-[0px_0px_8px_0px_rgba(0,0,0,0.08)] flex justify-between items-center"
            >
              <div className="flex items-center gap-4">
                <TicketCheck className="w-6 h-6 text-[#5DB075]" />
                <span className="text-black text-sm font-semibold font-inter">
                  {t("profile.enter_access_code")}
                </span>
              </div>
              <ChevronRight className="w-6 h-6 text-black" />
            </button>
          )}

          {/* Account and Help Center Sections */}
          <div className="flex flex-col gap-6">
            {/* Account Section */}
            <div className="flex flex-col gap-4">
              <h3 className="text-[#9a9a9a] text-xs font-bold font-inter uppercase tracking-wide">
                {t("profile.section_account")}
              </h3>
              <div className="flex flex-col gap-4">
                {/* Edit Profile Info */}
                <button
                  onClick={handleProfileClick}
                  className="px-4 py-3 bg-white rounded-xl shadow-[0px_0px_8px_0px_rgba(0,0,0,0.08)] flex justify-between items-center"
                >
                  <div className="flex items-center gap-4">
                    <User className="w-6 h-6 text-[#9A9A9A]" />
                    <span className="text-black text-sm font-normal font-inter">
                      {isProfileComplete
                        ? t("profile.edit_profile_info")
                        : t("profile.create_profile")}
                    </span>
                  </div>
                  <ChevronRight className="w-6 h-6 text-black" />
                </button>
                {/* Language Selector */}
                {!isMobileWeb && <LanguageSelector />}
                {/* Notification Settings */}
                {!isMobileWeb && (
                <button
                  onClick={() => navigate("/profile/notification-settings")}
                  className="px-4 py-3 bg-white rounded-xl shadow-[0px_0px_8px_0px_rgba(0,0,0,0.08)] flex justify-between items-center"
                >
                  <div className="flex items-center gap-4">
                    <Bell className="w-6 h-6 text-[#9A9A9A]" />
                    <span className="text-black text-sm font-normal font-inter">
                      {t("profile.notification_settings")}
                    </span>
                  </div>
                  <ChevronRight className="w-6 h-6 text-black" />
                </button>
                )}
                {showManagePayments && (
                  <button
                    onClick={() => navigate("/profile/manage-payments")}
                    className="px-4 py-3 bg-white rounded-xl shadow-[0px_0px_8px_0px_rgba(0,0,0,0.08)] flex justify-between items-center"
                  >
                    <div className="flex items-center gap-4">
                      <CreditCardIcon className="w-6 h-6 text-[#9A9A9A]" />
                      <span className="text-black text-sm font-normal font-inter">
                        {t("profile.manage_payments")}
                      </span>
                    </div>
                    <ChevronRight className="w-6 h-6 text-black" />
                  </button>
                )}
                {orgExamType && !isMobileWeb && (
                  <button
                    onClick={openRedeemCodeModal}
                    className="px-4 py-3 bg-white rounded-xl shadow-[0px_0px_8px_0px_rgba(0,0,0,0.08)] flex justify-between items-center"
                  >
                    <div className="flex items-center gap-4">
                      <TicketCheck className="w-6 h-6 text-[#9A9A9A]" />
                      <span className="text-black text-sm font-normal font-inter">
                        {t("profile.enter_access_code")}
                      </span>
                    </div>
                    <ChevronRight className="w-6 h-6 text-black" />
                  </button>
                )}
                {orgExamType && !isMobileWeb && (
                  <ShareEmployerLink examType={orgExamType} />
                )}
              </div>
            </div>

            {/* Help Center Section */}
            {!isMobileWeb && (
            <div className="flex flex-col gap-4">
              <h3 className="text-[#9a9a9a] text-xs font-bold font-inter uppercase tracking-wide">
                {t("profile.section_help")}
              </h3>
              <div className="flex flex-col gap-4">
                {/* FAQs */}
                <button
                  onClick={() => {
                    const url =
                      APP_TO_BUILD === "electrical_us"
                        ? "http://dakotaprep.com/faq"
                        : "https://dakotaprep.com/ca/faq";

                    window.open(url, "_blank", "noopener,noreferrer");
                  }}
                  className="px-4 py-3 bg-white rounded-xl shadow-[0px_0px_8px_0px_rgba(0,0,0,0.08)] flex justify-between items-center"
                >
                  <div className="flex items-center gap-4">
                    <Info className="w-6 h-6 text-[#9A9A9A]" />
                    <span className="text-black text-sm font-normal font-inter">
                      {t("profile.faqs")}
                    </span>
                  </div>
                  <ChevronRight className="w-6 h-6 text-black" />
                </button>

                {/* Need Help? */}
                <button
                  onClick={() => {
                    const url = "mailto:support@dakotaprep.com";
                    window.open(url, "_blank", "noopener,noreferrer");
                  }}
                  className="px-4 py-3 bg-white rounded-xl shadow-[0px_0px_8px_0px_rgba(0,0,0,0.08)] flex justify-between items-center"
                >
                  <div className="flex items-center gap-4">
                    <HelpCircle className="w-6 h-6 text-[#9A9A9A]" />
                    <span className="text-black text-sm font-normal font-inter">
                      {t("profile.need_help")}
                    </span>
                  </div>
                  <ChevronRight className="w-6 h-6 text-black" />
                </button>

                <button
                  onClick={handleSignOut}
                  className="p-4 rounded-[100px] bg-red-600 text-white flex items-center justify-center mt-10"
                >
                  {t("auth.sign_out")}
                </button>

                {store.mainAppStore.isAdmin() && <AdminMode />}
              </div>
            </div>
            )}

            {isMobileWeb && (
              <button
                onClick={handleSignOut}
                className="p-4 rounded-[100px] bg-red-600 text-white flex items-center justify-center"
              >
                {t("auth.sign_out")}
              </button>
            )}
          </div>

          {isMobileWeb && (
            <footer className="flex items-center justify-center gap-2 text-xs text-[#6f6f6f]">
              <a
                href={PRIVACY_POLICY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:underline"
              >
                {t("common.privacy_policy")}
              </a>
              <span aria-hidden="true">|</span>
              <a
                href={TERMS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:underline"
              >
                {t("common.terms")}
              </a>
            </footer>
          )}
        </div>
      </div>
      {redeemCodeModal}
    </>
  );
});

export default ProfilePage;
